import type * as api from '@gitbook/api-client';
import { createPalette } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { OverlayTriggerState } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { flattenGroupedSections, type UseUpgradeSite } from '../../../state';
import { getPaidIndicatorBadgeProps } from '../PaidFeatureIndicator';
import { SiteSectionIcon } from '../SiteSectionIcon';
import { addSectionsToGroup } from './addSectionsToGroup';

export interface UseAddSectionsToGroupProps {
    organization: api.Organization;
    site: api.Site;
    siteStructure: api.SiteStructure;
    createSiteSectionDialogTrigger: OverlayTriggerState;
    upgrade: UseUpgradeSite | null;
}

export function useAddSectionsToGroup(props: UseAddSectionsToGroupProps) {
    const { createSiteSectionDialogTrigger, siteStructure, organization, site, upgrade } = props;
    return useEventCallback((siteSectionGroup: api.SiteSectionGroup) => {
        return createPalette({
            groups: [
                {
                    key: 'sections',
                    commands:
                        siteStructure.type === 'sections'
                            ? flattenGroupedSections(siteStructure.structure)
                                  .filter((section) => section.sectionGroup !== siteSectionGroup.id)
                                  .map((siteSection) => {
                                      return {
                                          key: siteSection.id,
                                          title: siteSection.title,
                                          icon: <SiteSectionIcon siteSection={siteSection} />,
                                          trigger: () =>
                                              addSectionsToGroup({
                                                  sectionGroup: siteSectionGroup,
                                                  siteSections: [siteSection],
                                                  organization,
                                                  site,
                                              }),
                                      };
                                  })
                            : [],
                },
                {
                    key: 'new-section',
                    commands: [
                        {
                            key: 'new-section',
                            title: 'New section',
                            icon: Icon.AddSiteSection,
                            trigger:
                                upgrade?.onPress ?? (() => createSiteSectionDialogTrigger.open()),
                            badge: getPaidIndicatorBadgeProps({ upgrade }),
                            upgrade: Boolean(upgrade),
                        },
                    ],
                },
            ],
        });
    });
}
